import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../i18n/useTranslation';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const authHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const ROLE_STYLES = {
  super: 'bg-purple-100 text-purple-700 border-purple-200',
  admin: 'bg-blue-100 text-blue-700 border-blue-200',
  user: 'bg-gray-100 text-gray-700 border-gray-200'
};

const emptyForm = { name: '', email: '', password: '', role: 'admin' };

const AdminUsers = ({ showToast }) => {
  const { t, language } = useTranslation();
  const { user: currentUser } = useAuth();
  const isRTL = language === 'ar';

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [passwordFor, setPasswordFor] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/users`, authHeaders());
      setUsers(res.data);
    } catch (error) {
      console.error('Error loading users:', error);
      showToast(t('admin.usersLoadError'), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API_URL}/users/${editingId}`, {
          name: form.name,
          email: form.email,
          role: form.role
        }, authHeaders());
        showToast(t('admin.userUpdated'), 'success');
      } else {
        await axios.post(`${API_URL}/users`, form, authHeaders());
        showToast(t('admin.userCreated'), 'success');
      }
      resetForm();
      load();
    } catch (error) {
      showToast(error.response?.data?.message || t('admin.userSaveError'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (u) => {
    setEditingId(u._id);
    setForm({ name: u.name, email: u.email, password: '', role: u.role });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleActive = async (u) => {
    try {
      await axios.put(`${API_URL}/users/${u._id}`, { isActive: !(u.isActive !== false) }, authHeaders());
      showToast(t('admin.userUpdated'), 'success');
      load();
    } catch (error) {
      showToast(error.response?.data?.message || t('admin.userSaveError'), 'error');
    }
  };

  const remove = async (u) => {
    if (!window.confirm(t('admin.userDeleteConfirm', { name: u.name }))) return;
    try {
      await axios.delete(`${API_URL}/users/${u._id}`, authHeaders());
      showToast(t('admin.userDeleted'), 'success');
      load();
    } catch (error) {
      showToast(error.response?.data?.message || t('admin.userSaveError'), 'error');
    }
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API_URL}/users/${passwordFor._id}/password`, { password: newPassword }, authHeaders());
      showToast(t('admin.passwordReset'), 'success');
      setPasswordFor(null);
      setNewPassword('');
    } catch (error) {
      showToast(error.response?.data?.message || t('admin.userSaveError'), 'error');
    }
  };

  const roleLabel = (role) => t(`admin.role.${role}`);

  return (
    <div className="space-y-4" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Create / edit form */}
      <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-1">
          {editingId ? t('admin.editUser') : t('admin.createUser')}
        </h2>
        <p className="text-sm text-gray-600 mb-4">{t('admin.usersDescription')}</p>

        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">{t('admin.userName')}</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">{t('admin.userEmail')}</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              dir="ltr"
            />
          </div>

          {!editingId && (
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">{t('admin.userPassword')}</label>
              <input
                type="text"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
                minLength={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                dir="ltr"
              />
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">{t('admin.userRole')}</label>
            <select
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white"
            >
              <option value="admin">{t('admin.role.admin')}</option>
              <option value="super">{t('admin.role.super')}</option>
              <option value="user">{t('admin.role.user')}</option>
            </select>
          </div>

          <div className="sm:col-span-2 flex flex-col sm:flex-row gap-2">
            <button
              type="submit"
              disabled={saving}
              className="w-full sm:w-auto px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium text-sm"
            >
              {saving ? t('admin.saving') : editingId ? t('admin.saveChanges') : t('admin.createUser')}
            </button>
            {editingId && (
              <button
                type="button"
                onClick={resetForm}
                className="w-full sm:w-auto px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium text-sm"
              >
                {t('cancel')}
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Account list */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="p-4 sm:p-6 border-b border-gray-100">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
            {t('admin.allUsers')} ({users.length})
          </h2>
        </div>

        {loading ? (
          <div className="p-10 flex justify-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
          </div>
        ) : users.length === 0 ? (
          <p className="p-8 text-center text-gray-500">{t('admin.noUsers')}</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {users.map(u => {
              const isSelf = u._id === currentUser?.id || u._id === currentUser?._id;
              const active = u.isActive !== false;

              return (
                <li key={u._id} className="p-4 sm:p-5 hover:bg-gray-50 transition">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                    {/* Identity */}
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <span className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0 ${
                        active ? 'bg-gradient-to-br from-blue-500 to-indigo-600' : 'bg-gray-400'
                      }`}>
                        {u.name?.charAt(0).toUpperCase()}
                      </span>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold text-gray-900 truncate">{u.name}</p>
                          {isSelf && (
                            <span className="text-[10px] font-bold px-2 py-0.5 bg-gray-900 text-white rounded-full">
                              {t('admin.you')}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 truncate" dir="ltr">{u.email}</p>
                      </div>
                    </div>

                    {/* Badges */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${ROLE_STYLES[u.role] || ROLE_STYLES.user}`}>
                        {roleLabel(u.role)}
                      </span>
                      <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${
                        active
                          ? 'bg-green-100 text-green-700 border-green-200'
                          : 'bg-red-100 text-red-700 border-red-200'
                      }`}>
                        {active ? t('admin.active') : t('admin.inactive')}
                      </span>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-wrap items-center gap-1.5 flex-shrink-0">
                      <button
                        onClick={() => startEdit(u)}
                        className="px-3 py-1.5 text-xs font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition"
                      >
                        {t('edit')}
                      </button>
                      <button
                        onClick={() => { setPasswordFor(u); setNewPassword(''); }}
                        className="px-3 py-1.5 text-xs font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
                      >
                        {t('admin.resetPassword')}
                      </button>
                      {!isSelf && (
                        <>
                          <button
                            onClick={() => toggleActive(u)}
                            className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition ${
                              active
                                ? 'text-amber-700 bg-amber-50 hover:bg-amber-100'
                                : 'text-green-700 bg-green-50 hover:bg-green-100'
                            }`}
                          >
                            {active ? t('admin.deactivate') : t('admin.activate')}
                          </button>
                          <button
                            onClick={() => remove(u)}
                            className="px-3 py-1.5 text-xs font-semibold text-red-700 bg-red-50 hover:bg-red-100 rounded-lg transition"
                          >
                            {t('delete')}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Password reset modal */}
      {passwordFor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-5 sm:p-6" dir={isRTL ? 'rtl' : 'ltr'}>
            <h3 className="text-lg font-bold text-gray-900 mb-1">{t('admin.resetPassword')}</h3>
            <p className="text-sm text-gray-600 mb-4">{passwordFor.name}</p>

            <form onSubmit={submitPassword}>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                {t('admin.newPassword')}
              </label>
              <input
                type="text"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
                autoFocus
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm mb-4"
                dir="ltr"
              />
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  type="submit"
                  className="w-full sm:flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-sm"
                >
                  {t('admin.saveChanges')}
                </button>
                <button
                  type="button"
                  onClick={() => setPasswordFor(null)}
                  className="w-full sm:flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium text-sm"
                >
                  {t('cancel')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
